import { Box } from "@/components/ui/box";
import { HStack } from "@/components/ui/hstack";
import { Text } from "@/components/ui/text";
import { Pressable } from "@/components/ui/pressable";
import { View } from "@/components/ui/view";
import { GlobalState } from "@/containers/GlobalState";

import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { backendRequest } from "@/stores/thunks";
import { FlatList } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Moment from 'moment';
import 'moment/locale/de';
import i18n from "i18n-js";
import Footer from '@/components/basic/Footer';
import WorkflowListElement from "@/components/workflow/WorkflowListElement";
import ModalCalendar from "../modals/ModalCalendar";
import { RRule, RRuleSet, rrulestr } from 'rrule';
import { switchScreen } from '@/hooks/navigate';
import inaLogo from "@/assets/icons/icon_blue_rounded_512x512.png"
import BasicElement from '@/components/basic/BasicElement';
import ImageSynced from '@/components/media/ImageSynced';
import { IconM } from '@/components/media/IconsV2';

const repeat = ['yearly', 'monthly', 'weekly', 'daily', 'once']

function DatePicker({ date, setDate, showCalender }) {
    return (
        <HStack className="p-2 justify-between">
            <Pressable onPress={() => setDate(Moment(date).subtract(1, 'd'))} className="mt-1">
                <IconM className="text-lg text-secondary-900" name='keyboard-arrow-left' />
            </Pressable>
            <Pressable onPress={() => showCalender()}>
                <HStack space='sm' className="items-center justify-center">
                    <IconM className="text-lg text-secondary-900" name='event-available' />
                    <Text className="font-bold text-secondary-900 text-xl leading-xl">{Moment(date).format('dd, DD. MMMM YYYY')}</Text>
                </HStack>
            </Pressable>
            <Pressable onPress={() => setDate(Moment(date).add(1, 'd'))} className="mt-1">
                <IconM className="text-lg text-secondary-900" name='keyboard-arrow-right' />
            </Pressable>
        </HStack>
    );
}

export default function Planner({ navigation, route }) {
    const { isEdit, contact } = route.params

    Moment.locale(i18n.t('calendar.dateLanguage'))
    const user = useSelector((state) => state.user)
    const workflows = useSelector((state) => state.workflows)
    const wfMap = useSelector((state) => state.wfMap || [])
    const planned = useSelector((state) => state.planned)
    const server = useSelector((state) => state.server)
    const insets = useSafeAreaInsets();
    const dispatch = useDispatch()
    const [planId, setPlanId] = useState(contact ? 'other' : 'plans')

    const [showModal, setShowModal] = useState(false)
    const [date, setDate] = useState((Moment().startOf('day').toISOString()))
    const [selectedDates, setSelectedDates] = useState([])
    const [plans, setPlans] = useState([])

    const selectToday = () => {
        setDate(Moment().startOf('day').toISOString())
    }

    const actions = [
        { icon: 'keyboard-arrow-left', text: 'footer.back', component: 'BACK' },
        { icon: 'home', text: 'footer.home', component: 'Home' },
        { icon: 'today', text: 'footer.today', component: 'Planner', params: { isEdit: isEdit, contact: contact }, exec: selectToday }
    ]

    const { inAColors } = useContext(GlobalState)

    useEffect(() => {
        if (contact) {
            setPlanId('other')
            dispatch(backendRequest('GET_PLAN_OTHER', '/planned/user/' + contact.toUser.id, null, 'GET', false))
        } else {
            setPlanId('plans')
            dispatch(backendRequest('GET_CONTACTS_INLINE', '/contact/user', null, 'GET', false))
        }
        navigation.setOptions({ title: i18n.t(contact ? 'plannerScreen.titleFor' : 'plannerScreen.title', { name: contact?.toUser.preferredName }) })
    }, [contact])

    const isValid = (workflowId) => {
        if (wfMap[workflowId] && workflows[wfMap[workflowId]]) {
            // workflow is known and can be displayed
            return !workflows[wfMap[workflowId]].deleted
        } else {
            return undefined != contact
        }
    }

    useEffect(() => {
        if (planned[planId]) {
            let plans = planned[planId]
                .filter(plan => isValid(plan.workflowId))
                .filter(plan => rrulestr(plan.schedule).between(new Date(Moment(date).subtract(1, 'ms')), new Date(Moment(date).endOf('day'))).length > 0)
                .map(plan => ({ ...plan, taskid: wfMap[plan.workflowId] }))
                .sort((a, b) => a.startAt > b.startAt ? 1 : a.startAt < b.startAt ? -1 : 0)
            // console.log('PLANS', planId, plans)
            const plansO = plans.filter(plan => plan.createdBy.id != user.id)
            if (plansO.length > 0) {
                plans = plans.filter(plan => plan.createdBy.id == user.id)
                if (plans.length > 0) {
                    plans.unshift({ info: i18n.t(contact ? 'plannerScreen.plannedFor' : 'plannerScreen.plannedOwn', { num: plans.length, name: contact?.toUser.preferredName }) })
                }
                plans.push({ info: i18n.t(contact ? 'plannerScreen.plannedBy' : 'plannerScreen.plannedForMe', { num: plansO.length, name: contact?.toUser.preferredName }) })
                plans = plans.concat(plansO)
            } else {
                if (plans.length > 0) {
                    plans.unshift({ info: i18n.t(contact ? 'plannerScreen.plannedFor' : 'plannerScreen.plannedOwn', { num: plans.length, name: contact?.toUser.preferredName }) })
                }
            }
            setPlans(plans)
        }
    }, [date, planned[planId]])

    const updateCalenderDate = (newDate) => {
        setDate(newDate.startOf('day'))
    }

    const onMonthChange = (newDate) => {
        setSelectedDates(rulesToSelected(newDate))
    }

    const rulesToSelected = (actDate) => {
        return planned[planId]
            .filter(plan => isValid(plan.workflowId))
            .map(plan => rrulestr(plan.schedule).between(new Date(Moment(actDate).startOf('month')), new Date(Moment(actDate).endOf('month'))))
            .flat().map(day => ({
                date: day,
                textStyle: { color: inAColors.secondary["900"] },
                style: { backgroundColor: inAColors.primary["100"] },
                allowDisabled: true,
            }))
    }

    const showCalender = () => {
        setSelectedDates(rulesToSelected(date))
        setShowModal(true)
    }

    const addWorkflow = () => {
        switchScreen(navigation, 'SelectWorkflow', { startDate: typeof date == 'string' ? date : date.toISOString(), contact: contact })
    }

    const onPress = (taskid, context) => {
        console.log('CONTEXT', context)
        const task = workflows[taskid]
        switchScreen(navigation, 'ScheduleWorkflow', { task: task, plans: plans.filter(plan => plan.workflowId == task.workflowId && plan.createdBy.id == user.id), contact: contact, planId: context.planId })
    }

    const renderItem = ({ item, index }) => {
        let freq = i18n.t('scheduleWorkflow.' + repeat[4]).toLowerCase()
        let info
        if (item.schedule) {
            const rule = rrulestr(item.schedule)
            if (rule.options.freq != 3 || rule.options.count != 1) {
                freq = i18n.t('scheduleWorkflow.' + repeat[rule.options.freq]).toLowerCase()
            }
            const time = Moment(rule.options.dtstart).format("HH:mm")
            info = i18n.t(time == '00:00' ? 'scheduleWorkflow.allDaySchedule' : 'scheduleWorkflow.schedule', { time: time, freq: freq })
        }
        return item.info ?
            <Text key={'T' + index} className="px-5 text-xl text-secondary-900">{item.info}</Text>
            : (item.taskid && (!contact || item.createdBy.id == user.id) ?
                <WorkflowListElement navigation={navigation} taskid={item.taskid} showProgress={false} showTimer={false} onPress={onPress} disabled={!server.online || !isEdit || item.createdBy.id != user.id} info={info} context={{planId: item?.id}} />
                : <Box className="mx-3">
                    <BasicElement media={inaLogo} prefix='' title={i18n.t('plannerScreen.placeHolder')} text={info} disabled={false} mark1={false} >
                        <Box className="border-primary-900 border rounded-full self-end p-1">
                            <ImageSynced key={contact?.toUser.id} uri={contact?.toUser.id} isAvatar={true} alignSelf='flex-end' size='sm' bg='white' />
                        </Box>
                    </BasicElement>
                </Box>
            );
    }

    return (
        <View className="flex-1 bg-secondary-50">
            <FlatList
                contentContainerStyle={{ paddingBottom: server.online ? 140 + insets.bottom : 90 + insets.bottom }}
                data={plans}
                renderItem={renderItem}
                keyExtractor={(item, index) => item.id || 'T' + index}
                ListHeaderComponent={<DatePicker date={date} setDate={setDate} showCalender={showCalender} />}
            />
            <ModalCalendar showModal={showModal} setShowModal={setShowModal} date={date} onDateChange={updateCalenderDate} onMonthChange={onMonthChange} customDatesStyles={selectedDates} />
            <Footer navigation={navigation} actions={actions} button={server.online && isEdit ? { action: addWorkflow, info: 'plannerScreen.addWorkflow' } : null} />
        </View>
    );
}
